import { useEffect, useRef, useState } from 'react';

interface Veiculo {
  nome: string;
  logo: string;
  tipo: 'Mencionado' | 'Entrevista' | 'Matéria';
  ano: string;
}

const veiculos: Veiculo[] = [
  {
    nome: 'Revista Veja',
    logo: 'ri-newspaper-line',
    tipo: 'Mencionado',
    ano: '2023',
  },
  {
    nome: 'Estadão',
    logo: 'ri-article-line',
    tipo: 'Matéria',
    ano: '2023',
  },
  {
    nome: 'Folha de S.Paulo',
    logo: 'ri-file-text-line',
    tipo: 'Mencionado',
    ano: '2024',
  },
  {
    nome: 'Podcast Flow',
    logo: 'ri-mic-line',
    tipo: 'Entrevista',
    ano: '2024',
  },
  {
    nome: 'Marie Claire',
    logo: 'ri-book-open-line',
    tipo: 'Matéria',
    ano: '2023',
  },
  {
    nome: 'Glamour Brasil',
    logo: 'ri-star-line',
    tipo: 'Mencionado',
    ano: '2024',
  },
];

interface Depoimento {
  nome: string;
  cargo: string;
  foto: string;
  texto: string;
  instagram: string;
}

const depoimentos: Depoimento[] = [
  {
    nome: 'Ana Paula Padrão',
    cargo: 'Jornalista e Apresentadora',
    foto: 'https://public.readdy.ai/ai/img_res/9da5625af3ef322ebd02a07de1fce721.jpg',
    texto: 'O método da Bia é revolucionário. A forma como ela une técnicas de PNL com escrita terapêutica é única no Brasil.',
    instagram: '@anapaulapadrao',
  },
  {
    nome: 'Dr. Augusto Cury',
    cargo: 'Psiquiatra e Escritor',
    foto: 'https://readdy.ai/api/search-image?query=Distinguished%20male%20psychiatrist%20in%20his%2060s%20wearing%20elegant%20suit%20warm%20compassionate%20expression%20professional%20portrait%20soft%20studio%20lighting%20neutral%20background%20high%20quality%20medical%20professional%20headshot&width=200&height=200&seq=augusto-cury-001&orientation=squarish',
    texto: 'Bia Holmes está transformando vidas com um método que combina ciência e sensibilidade. Seu trabalho é inspirador.',
    instagram: '@drcury',
  },
  {
    nome: 'Flávia Alessandra',
    cargo: 'Atriz e Influenciadora',
    foto: 'https://readdy.ai/api/search-image?query=Beautiful%20elegant%20Brazilian%20actress%20in%20her%2040s%20radiant%20smile%20warm%20expression%20professional%20portrait%20soft%20natural%20lighting%20neutral%20background%20high%20quality%20celebrity%20headshot&width=200&height=200&seq=flavia-alessandra-001&orientation=squarish',
    texto: 'Escrever para o meu eu futuro mudou minha perspectiva. O Carta do Eu Futuro é um presente que você dá para si mesmo.',
    instagram: '@flaviaalessandra',
  },
];

const BiaNaMidia = () => {
  const [stage, setStage] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const timers = [
            setTimeout(() => setStage(1), 100),
            setTimeout(() => setStage(2), 400),
            setTimeout(() => setStage(3), 700),
            setTimeout(() => setStage(4), 1000),
          ];
          return () => timers.forEach(clearTimeout);
        }
      },
      { threshold: 0.15 },
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
      observer.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 bg-luxury-black overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-24 left-1/4 w-2 h-2 bg-gold-500/30 rounded-full animate-float" />
      <div
        className="absolute top-48 right-1/4 w-1.5 h-1.5 bg-gold-400/20 rounded-full animate-float"
        style={{ animationDelay: '2s' }}
      />
      <div
        className="absolute bottom-32 left-1/2 w-1 h-1 bg-gold-300/30 rounded-full animate-float"
        style={{ animationDelay: '3.5s' }}
      />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Header */}
        <div
          className="max-w-3xl mx-auto text-center mb-20 transition-all duration-700 ease-out"
          style={{
            opacity: stage >= 1 ? 1 : 0,
            transform: stage >= 1 ? 'translateY(0)' : 'translateY(-20px)',
          }}
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-gold-500/50" />
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-400/70">
              Bia na Mídia
            </span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-gold-500/50" />
          </div>

          <h2 className="font-['Playfair_Display'] text-4xl lg:text-6xl text-white font-light leading-tight mb-6">
            Reconhecida por{' '}
            <span className="italic text-gold-400">especialistas</span>
          </h2>

          <p className="font-['Montserrat'] text-sm lg:text-base text-white/45 leading-relaxed font-light tracking-wide">
            O trabalho de transformação pessoal da Bia Holmes tem sido destaque em grandes veículos 
            de comunicação e validado por especialistas renomados.
          </p>
        </div>

        {/* Grid de veículos */}
        <div
          className="max-w-6xl mx-auto mb-24 transition-all duration-700 ease-out"
          style={{
            opacity: stage >= 2 ? 1 : 0,
            transform: stage >= 2 ? 'translateY(0)' : 'translateY(25px)',
          }}
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {veiculos.map((veiculo, index) => (
              <div
                key={index}
                className="group relative transition-all duration-500 ease-out"
                style={{
                  opacity: stage >= 2 ? 1 : 0,
                  transform: stage >= 2 ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.9)',
                  transitionDelay: `${index * 80}ms`,
                }}
              >
                <div className="relative h-full bg-luxury-charcoal/40 backdrop-blur-sm border border-gold-500/10 rounded-xl p-6 hover:border-gold-500/30 transition-all duration-500 hover:shadow-[0_8px_30px_rgba(201,168,76,0.12)] overflow-hidden flex flex-col items-center justify-center text-center">
                  {/* Glow on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gold-500/0 to-gold-500/0 group-hover:from-gold-500/5 group-hover:to-gold-500/10 transition-all duration-500 rounded-xl" />

                  {/* Icon */}
                  <div className="relative mb-3">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gold-500/20 to-gold-600/10 border border-gold-500/20 flex items-center justify-center group-hover:scale-110 transition-all duration-500">
                      <i className={`${veiculo.logo} text-2xl text-gold-400 w-6 h-6 flex items-center justify-center`} />
                    </div>
                  </div>

                  {/* Nome */}
                  <h4 className="font-['Montserrat'] text-xs text-white font-semibold mb-2 leading-tight">
                    {veiculo.nome}
                  </h4>

                  {/* Badge tipo */}
                  <span className="inline-block bg-gold-500/10 border border-gold-500/20 rounded-full px-2 py-0.5 font-['Montserrat'] text-[9px] text-gold-400/70 tracking-wider mb-1">
                    {veiculo.tipo}
                  </span>

                  {/* Ano */}
                  <span className="font-['Montserrat'] text-[9px] text-white/30 tracking-wider">
                    {veiculo.ano}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Depoimentos de influenciadores */}
        <div
          className="max-w-6xl mx-auto transition-all duration-700 ease-out"
          style={{
            opacity: stage >= 3 ? 1 : 0,
            transform: stage >= 3 ? 'translateY(0)' : 'translateY(25px)',
          }}
        >
          <h3 className="font-['Playfair_Display'] text-3xl text-white font-light text-center mb-12">
            O que dizem sobre o método
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {depoimentos.map((depoimento, index) => (
              <div
                key={index}
                className="group relative transition-all duration-500 ease-out"
                style={{
                  opacity: stage >= 4 ? 1 : 0,
                  transform: stage >= 4 ? 'translateY(0)' : 'translateY(25px)',
                  transitionDelay: `${index * 150}ms`,
                }}
              >
                <div className="relative h-full bg-gradient-to-br from-luxury-charcoal/60 to-luxury-charcoal/40 backdrop-blur-sm border border-gold-500/10 rounded-2xl p-8 hover:border-gold-500/30 transition-all duration-500 hover:shadow-[0_8px_30px_rgba(201,168,76,0.15)] overflow-hidden">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gold-500/0 to-gold-500/0 group-hover:from-gold-500/5 group-hover:to-gold-500/10 transition-all duration-500 rounded-2xl" />

                  {/* Quote icon */}
                  <div className="absolute top-6 right-6 w-10 h-10 bg-gold-500/10 rounded-full flex items-center justify-center">
                    <i className="ri-double-quotes-r text-xl text-gold-400/40 w-5 h-5 flex items-center justify-center" />
                  </div>

                  {/* Foto */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 rounded-full border-2 border-gold-500/30 overflow-hidden group-hover:scale-105 transition-all duration-500">
                      <img
                        src={depoimento.foto}
                        alt={depoimento.nome}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Texto */}
                  <p className="font-['Montserrat'] text-sm text-white/60 leading-relaxed font-light tracking-wide mb-6 italic">
                    "{depoimento.texto}"
                  </p>

                  {/* Nome e cargo */}
                  <div className="relative">
                    <h4 className="font-['Playfair_Display'] text-lg text-white font-semibold mb-1">
                      {depoimento.nome}
                    </h4>
                    <p className="font-['Montserrat'] text-xs text-gold-400/70 tracking-wide mb-2">
                      {depoimento.cargo}
                    </p>
                    <a
                      href={`https://instagram.com/${depoimento.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1 font-['Montserrat'] text-[10px] text-gold-400/50 hover:text-gold-400 transition-colors duration-300 cursor-pointer tracking-wide"
                    >
                      <i className="ri-instagram-line text-xs w-3 h-3 flex items-center justify-center" />
                      {depoimento.instagram}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom decorative line */}
        <div className="flex items-center justify-center gap-4 mt-20">
          <div className="w-24 h-px bg-gradient-to-r from-transparent to-gold-500/30" />
          <i className="ri-newspaper-line text-gold-500/40 text-xl w-6 h-6 flex items-center justify-center" />
          <div className="w-24 h-px bg-gradient-to-l from-transparent to-gold-500/30" />
        </div>
      </div>
    </section>
  );
};

export default BiaNaMidia;